import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { X } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import CinemaHero from '../components/gallery/cinema/CinemaHero';
import CinemaFooter from '../components/gallery/cinema/CinemaFooter';
import { MasonryGrid } from '../components/MasonryGrid';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const PortfolioGallery = () => {
  const { galleryId } = useParams();
  const [gallery, setGallery] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  useEffect(() => {
    loadGallery();
  }, [galleryId]);

  const loadGallery = async () => {
    try {
      setLoading(true);
      
      const [galleryRes, photosRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/portfolio/gallery/${galleryId}`),
        axios.get(`${BACKEND_URL}/api/portfolio/gallery/${galleryId}/photos`)
      ]);
      
      setGallery(galleryRes.data);
      setPhotos(Array.isArray(photosRes.data) ? photosRes.data : []);
    } catch (error) {
      console.error('Error loading gallery:', error);
      toast.error('Gallery not found');
    } finally {
      setLoading(false);
    }
  };

  const openLightbox = (index) => {
    setCurrentPhotoIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const nextPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev + 1) % photos.length);
  };

  const prevPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev - 1 + photos.length) % photos.length);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') nextPhoto();
      if (e.key === 'ArrowLeft') prevPhoto();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, photos.length]);

  if (loading) {
    return (
      <div className="min-h-screen bg-tiq-nearblack flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block w-12 h-12 rounded-full animate-spin mb-4" style={{ border: '3px solid rgba(191,33,47,0.25)', borderTopColor: '#BF212F' }}></div>
          <p className="font-tiq-mono tiq-kicker" style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>Loading gallery…</p>
        </div>
      </div>
    );
  }

  if (!gallery) {
    return (
      <div className="min-h-screen bg-tiq-nearblack flex items-center justify-center text-white">
        <div className="text-center px-6">
          <h1 className="font-tiq-display uppercase mb-2" style={{ fontWeight: 900, fontSize: 32, letterSpacing: '-0.02em' }}>Gallery not found</h1>
          <p style={{ color: 'rgba(255,255,255,0.55)' }}>This portfolio gallery does not exist or has been removed.</p>
        </div>
      </div>
    );
  }

  const heroKicker = [
    gallery.event_date
      ? new Date(gallery.event_date).toLocaleDateString('en-AU', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })
      : null,
    `${photos.length} ${photos.length === 1 ? 'Photo' : 'Photos'}`,
  ].filter(Boolean).join(' · ');

  return (
    <div
      className="min-h-screen bg-tiq-nearblack font-tiq-body"
      onContextMenu={(e) => e.preventDefault()}
      onDragStart={(e) => e.preventDefault()}
    >
      <Toaster position="top-center" richColors />

      {/* Cinematic hero — read-only portfolio */}
      <CinemaHero
        imageUrl={gallery.featured_photo_url}
        focalPoint={gallery.featured_photo_focal_point}
        name={gallery.name}
        kicker={heroKicker}
        fallback={!gallery.featured_photo_url}
      />

      {/* Photo Grid — tight, read-only (no downloads) */}
      <main style={{ position: 'relative', zIndex: 2, background: '#0A0A0B' }}>
        <div style={{ padding: 'clamp(28px,4vw,48px) clamp(16px,3vw,38px) clamp(40px,6vw,64px)' }}>
          {photos.length === 0 ? (
            <div className="text-center" style={{ padding: '80px 20px' }}>
              <p className="font-tiq-mono tiq-kicker" style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)' }}>No photos in this gallery yet</p>
            </div>
          ) : (
            <MasonryGrid
              photos={photos}
              onPhotoClick={(_photo, index) => openLightbox(index)}
              backendUrl={BACKEND_URL}
              theme="dark"
              showActions={false}
              protectImages
            />
          )}
        </div>
        <CinemaFooter />
      </main>

      {/* Lightbox (NO DOWNLOAD BUTTON) */}
      {lightboxOpen && photos[currentPhotoIndex] && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-95 flex items-center justify-center">
          {/* Close Button */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 z-10 bg-white bg-opacity-10 hover:bg-opacity-20 text-white rounded-full p-2 transition-all"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Photo Counter */}
          <div className="absolute top-4 left-4 z-10 bg-white bg-opacity-10 text-white px-4 py-2 rounded-full">
            {currentPhotoIndex + 1} / {photos.length}
          </div>

          {/* Previous Button */}
          {photos.length > 1 && (
            <button
              onClick={prevPhoto}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-white bg-opacity-10 hover:bg-opacity-20 text-white rounded-full p-3 transition-all"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}

          {/* Next Button */}
          {photos.length > 1 && (
            <button
              onClick={nextPhoto}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-white bg-opacity-10 hover:bg-opacity-20 text-white rounded-full p-3 transition-all"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}

          {/* Image */}
          <div className="max-w-7xl max-h-screen w-full h-full p-4 flex items-center justify-center">
            <img
              src={photos[currentPhotoIndex].preview_url || photos[currentPhotoIndex].original_url}
              alt={`Photo ${currentPhotoIndex + 1}`}
              className="max-w-full max-h-full object-contain"
              onContextMenu={(e) => e.preventDefault()}
              draggable={false}
              style={{ WebkitTouchCallout: 'none', userSelect: 'none' }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default PortfolioGallery;
